/** The QueryViz report the user has open, for the Nucleus assistant's
 *  use_report tool (core features/nucleus/tools/use_report.py).
 *
 *  Sent with every turn as `uiContext.active_report`, derived from the LIVE
 *  app URL: on a page hosting a report embed the URL IS the view — the
 *  adapter mirrors every in-frame input change into the host URL
 *  (queryviz:params-changed → search string, quick filter → path segment),
 *  so slug + query params + quick filter reproduce exactly what the user
 *  sees. Core validates the params against the report's own guide and, when
 *  it reconfigures the view, hands back a link built from `link` here — the
 *  same grammar the router reads — which the dock navigates to (remounting
 *  the embed).
 *
 *  Hosts are listed explicitly: a report browser carries the slug in its
 *  path (/reporting/<slug>/<qf>?); a pinned tab keeps its own path and takes
 *  the quick filter as ?qf= (adapter syncReportSlug=false). No host pins one
 *  today — /demand stopped when its bare route became the native pivot. Keep in
 *  step with routes.tsx and ReportingView/constants.ts. */
import {
  DEMAND_BASE_PATH,
  QUERYVIZ_REPORT_BASE_PATH,
  QUERYVIZ_PROJECT_SLUG,
  reportProjectFromSearch,
} from "./containers/ReportingView/constants";

export interface ActiveReport {
  /** QueryViz report slug (the last segment of its canonical path). */
  slug: string;
  /** The actual embed project; separate from the report's SQL controls. */
  project: string;
  /** The user's current overrides (the host URL's query, minus qf). */
  params: Record<string, string>;
  /** Saved quick filter in effect, when the URL carries one. */
  quick_filter?: string;
  /** How to rebuild a link to this view: the path to prefix, and whether
   *  the quick filter rides as a path segment or a ?qf= param. */
  link: { base: string; quick_filter: "path" | "query" };
}

interface ReportHost {
  /** App route prefix hosting the embed. */
  base: string;
  /** Report slug when the path carries none (a pinned tab); a browser has none. */
  pinned?: string;
  /** Only hosts backed by ReportingView honor its own-company draft selector. */
  supportsProjectSelector?: boolean;
}

const HOSTS: ReportHost[] = [
  { base: QUERYVIZ_REPORT_BASE_PATH, supportsProjectSelector: true },
  // No pinned slug: bare /demand is the native pivot and hosts no embed, so it
  // has no active report. Its slugged reports still carry theirs in the path.
  { base: DEMAND_BASE_PATH, supportsProjectSelector: true },
  // The trackers page shows the Time Series Trends report inside the tracker
  // embed: an answer anchored to that report highlights it there too.
  { base: "/trackers", pinned: "TimeSeriesTrends" },
];

const SLUG = /^[A-Za-z0-9_-]+$/;

const segmentsUnder = (pathname: string, base: string): string[] | null => {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (path === base) return [];
  if (!path.startsWith(`${base}/`)) return null;
  return path
    .slice(base.length + 1)
    .split("/")
    .filter(Boolean);
};

/** The active report for `location`, or null when the page hosts none
 *  (including the bare /reporting landing list, which shows no report). */
export function activeReportFromLocation(location: {
  pathname: string;
  search: string;
}): ActiveReport | null {
  for (const host of HOSTS) {
    const segments = segmentsUnder(location.pathname, host.base);
    if (segments === null) continue;
    if (segments.some((s) => !SLUG.test(s))) return null;
    const [pathSlug, pathQf] = segments;
    // A browser (no pinned report) needs a slug in the path; a pinned tab
    // shows its own report unless the path names another one.
    const slug = pathSlug ?? host.pinned;
    if (!slug) return null;
    const params: Record<string, string> = {};
    const search = new URLSearchParams(location.search);
    const project = host.supportsProjectSelector
      ? reportProjectFromSearch(search)
      : QUERYVIZ_PROJECT_SLUG;
    search.forEach((v, k) => {
      // draft is a provenance hint in draft_url, not an embed input. Lifecycle
      // actions use the draft card's id; they do not read this page query field.
      if (k !== "qf" && k !== "project" && k !== "draft") params[k] = v;
    });
    // A slugged path carries the quick filter as its second segment; a
    // pinned tab's own path (no slug) takes it from ?qf=.
    const queryQf = search.get("qf");
    const quickFilter = pathSlug ? pathQf : queryQf && SLUG.test(queryQf) ? queryQf : undefined;
    const base = pathSlug ? `${host.base}/${pathSlug}` : host.base;
    return {
      slug,
      project,
      params,
      ...(quickFilter ? { quick_filter: quickFilter } : {}),
      link: { base, quick_filter: pathSlug ? "path" : "query" },
    };
  }
  return null;
}
